import omit from 'lodash/omit';
import { MouseEventHandler, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Button, Nav, NavItem } from 'react-bootstrap';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import { BsCaretRightFill, BsCaretLeftFill } from 'react-icons/bs';
import { Link, useLocation } from 'react-router-dom';

import './tabs-navbar.scss';

const LeftArrow = () => {
  const { isFirstItemVisible, scrollPrev } = useContext(VisibilityContext);

  return (
    <>
      {!isFirstItemVisible && (
        <Button
          variant="light"
          onClick={() => scrollPrev()}
          className="tabs-navbar-arrow-container tabs-navbar-arrow-container-left shadow"
        >
          <BsCaretLeftFill size={20} />
        </Button>
      )}
    </>
  );
};

const RightArrow = () => {
  const { isLastItemVisible, scrollNext } = useContext(VisibilityContext);

  return (
    <>
      {!isLastItemVisible && (
        <Button
          variant="light"
          onClick={() => scrollNext()}
          className="tabs-navbar-arrow-container tabs-navbar-arrow-container-right"
        >
          <BsCaretRightFill size={20} />
        </Button>
      )}
    </>
  );
};

const TabItem = (
  props:
    | ({
        itemID?: string;
        to: string;
        children: ReactNode;
      } & Parameters<typeof Nav.Link>[0])
    | ({
        itemID: string;
        onClick: MouseEventHandler<HTMLElement>;
        active: boolean;
        children: ReactNode;
      } & Parameters<typeof Nav.Link>[0])
) => {
  const location = useLocation();
  const noTrailingSlash = location.pathname.replace(/\/$/, '');

  if (props?.onClick) {
    return (
      <NavItem id={props.itemID}>
        <Nav.Link
          active={noTrailingSlash === props?.to}
          style={{ whiteSpace: 'nowrap' }}
          {...omit(props, 'children')}
        >
          {props?.children}
        </Nav.Link>
      </NavItem>
    );
  }

  if (props?.to) {
    return (
      <NavItem id={props.itemID}>
        <Nav.Link
          style={{ whiteSpace: 'nowrap' }}
          as={Link}
          active={noTrailingSlash === props?.to}
          to={props.to}
          {...omit(props, ['children', 'to', 'active'])}
        >
          {props.children}
        </Nav.Link>
      </NavItem>
    );
  }

  throw new Error('TabItem must have either a to or onClick prop');
};

export const TabsNavbar = ({
  tabs,
  className,
  variant = 'default',
  vertical = false,
  alignment,
}: {
  tabs: (
    | ({
        label: ReactNode;
        to: string;
      } & Parameters<typeof Nav.Link>[0])
    | ({
        itemID?: string;
        onClick?: () => void;
        active?: boolean;
      } & Parameters<typeof Nav.Link>[0])
  )[];
  variant?: 'default' | 'primary-dark';
  alignment?: 'left' | 'center' | 'right';
  vertical?: boolean;
  className?: string;
  center?: boolean;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiRef = useRef<any>(null);
  const [allTabsVisible, setAllTabsVisible] = useState(true);
  const { pathname } = useLocation();
  const pathnameWithoutTrailingSlash = pathname.replace(/\/$/, '');

  useEffect(() => {
    if (apiRef.current.initComplete) {
      const activeItem = apiRef.current.getItemById(pathnameWithoutTrailingSlash);
      apiRef.current.scrollToItem(activeItem);
    }
  }, [pathnameWithoutTrailingSlash, apiRef.current]);

  return (
    <ScrollMenu
      apiRef={apiRef}
      wrapperClassName={`pb-2 tabs-navbar tabs-variant-${variant} ${
        allTabsVisible && alignment ? `tabs-navbar-${alignment}` : ''
      } ${vertical ? 'tabs-navbar-vertical' : ''}`}
      scrollContainerClassName={`nav nav-tabs ${className ? className : ''}`}
      onUpdate={(visibility) => {
        if (visibility.visibleElements.length === tabs.length) {
          setAllTabsVisible(true);
        } else {
          setAllTabsVisible(false);
        }
      }}
      LeftArrow={LeftArrow}
      RightArrow={RightArrow}
    >
      {tabs.map(({ label, itemID, to, onClick, active, ...rest }, i) => (
        <TabItem
          key={itemID || to}
          itemID={itemID || to || i.toString()}
          to={to}
          onClick={onClick}
          active={active}
          {...rest}
        >
          {label}
        </TabItem>
      ))}
    </ScrollMenu>
  );
};

TabsNavbar.Item = TabItem;
